import { useState } from "react";
import { createPortal } from "react-dom";
import { useLang } from "@/context/LanguageContext";
import { useEditMode } from "@/context/EditModeContext";
import { Pencil, X } from "lucide-react";

const LANG_LABEL = { en: "English", cs: "Čeština", he: "עברית", ru: "Русский" };

export function EditableText({ k, as: Tag = "span", className = "", multiline = false, children, ...rest }) {
  const { t } = useLang();
  const em = useEditMode();
  if (!em.editing) {
    const text = t(k);
    return <Tag className={className} {...rest}>{text || children}</Tag>;
  }
  return <EditableInner k={k} Tag={Tag} className={className} multiline={multiline} rest={rest} />;
}

function EditableInner({ k, Tag, className, multiline, rest }) {
  const { tForLang } = useLang();
  const { editLang, saveText } = useEditMode();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const current = tForLang(editLang, k);
  const rtl = editLang === "he";

  const openEditor = (e) => { e.preventDefault(); e.stopPropagation(); setVal(current); setOpen(true); };
  const save = async () => { await saveText(k, val); setOpen(false); };

  return (
    <>
      <Tag
        {...rest}
        onClick={openEditor}
        data-testid={`editable-${k}`}
        title="Click to edit this text"
        className={`${className} relative cursor-pointer rounded-md outline outline-1 outline-dashed outline-[#BE923C]/70 hover:outline-[#BE923C] hover:bg-[#BE923C]/10 transition-colors`}
        style={{ outlineOffset: "3px" }}
      >
        {current}
        <span className="pointer-events-none absolute -top-2.5 -right-2.5 grid place-items-center w-5 h-5 rounded-full bg-[#BE923C] text-white shadow">
          <Pencil size={11} />
        </span>
      </Tag>
      {open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} dir="ltr">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#0F172A]">Edit text · <span className="text-[#BE923C]">{LANG_LABEL[editLang]}</span></h3>
              <button onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X size={18} /></button>
            </div>
            {multiline ? (
              <textarea data-testid="editable-input" rows={4} value={val} dir={rtl ? "rtl" : "ltr"}
                onChange={(e) => setVal(e.target.value)}
                className="w-full rounded-xl border border-[#0F172A]/15 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#BE923C]/40" autoFocus />
            ) : (
              <input data-testid="editable-input" value={val} dir={rtl ? "rtl" : "ltr"}
                onChange={(e) => setVal(e.target.value)}
                className="w-full rounded-xl border border-[#0F172A]/15 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#BE923C]/40" autoFocus />
            )}
            <p className="mt-2 text-xs text-[#94A3B8]">Editing the <b>{LANG_LABEL[editLang]}</b> version. Switch language in the bottom toolbar to translate.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded-full px-4 py-2 text-sm font-medium text-[#475569] hover:bg-[#0F172A]/5">Cancel</button>
              <button data-testid="editable-save" onClick={save} className="rounded-full bg-[#BE923C] px-5 py-2 text-sm font-semibold text-white hover:bg-[#a67e31]">Save</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
